import { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { updateAccount, getPracticesByNumbers, getNumberCandidates } from "../../api";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import InputGroup from "react-bootstrap/InputGroup";
import { FaPlus, FaTrash } from "react-icons/fa";
import React from "react";
import { useNavigate } from "react-router-dom";
import MedicalAidsReferenceModal from "./MedicalAidsReferenceModal";
import { useToast } from "../Toasts/useToast";
import AppToast from "../Toasts/AppToast";

function AccountEditModal({ account, show, onHide }) {
  const navigate = useNavigate();
  const [accountId, setAccountId] = useState("1");
  const [postalAddress, setPostalAddress] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const [userId, setUserId] = useState("");
  const [isSysAdmin, setIsSysAdmin] = useState(false);
  const [showMedAidRef, setShowMedAidRef] = useState(false);
  const [practices, setPractices] = useState([]);
  const [numberCandidates, setNumberCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [validationError, setValidationError] = useState("");

  const isMethod3 = practices.some(
    (p) => p.numberingConfig?.method === "surname"
  );

  // Account Information State
  const [patientAccount, setPatientAccount] = useState(account);

  function handleChangeAccount(e) {
    const { name, value } = e.target;
    const token = sessionStorage.getItem("user");
    const decoded = jwtDecode(token);
    setUserId(decoded._id);
    setPatientAccount((prevPatientAccount) => ({
      ...prevPatientAccount,
      [e.target.name]: e.target.value,
      accountCreatedBy: userId,
    }));

    if (name === "accountPostal") {
      // If the patient have a medical aid show medical aid fields
      setPostalAddress(true);
    }
  }

  async function handleSave() {
    setValidationError("");

    let numberToSave = patientAccount.accountNumber;

    // NUM-07: Method 3 number generation on edit when account has no number
    if (isMethod3 && !numberToSave) {
      const surname = patientAccount.accountLastName?.trim();
      if (!surname) {
        setValidationError("Last name required to generate account number");
        return;
      }

      try {
        const result = await getNumberCandidates(account._id, surname);
        if (!result) {
          showToast("error", "Could not generate account number. Please try again.");
          return;
        }
        if (result.isExisting) {
          // NUM-11 idempotency: account already has a number in DB
          numberToSave = result.accountNumber;
        } else if (result.candidates?.length === 1) {
          numberToSave = result.candidates[0].candidate;
        } else if (result.candidates?.length > 1) {
          // Multi-practice: show radio selection UI
          setNumberCandidates(result.candidates);
          setSelectedCandidate(result.candidates[0].candidate);
          return; // Do not save yet — wait for user to select
        }
      } catch (err) {
        showToast("error", "Could not generate account number. Please try again.");
        return;
      }
    }

    // If candidate selection was shown and user clicks Save again
    if (numberCandidates.length > 0 && selectedCandidate) {
      numberToSave = selectedCandidate;
    }

    const payload = { ...patientAccount, accountNumber: numberToSave };

    try {
      await updateAccount(account._id, payload);
      showToast("success", "Account updated successfully");
      // Clear candidate state
      setNumberCandidates([]);
      setSelectedCandidate(null);
      onHide();
      setTimeout(() => navigate(0), 1500);
      setPatientAccount({
        accountTitle: "",
        accountNumber: "",
        accountFullNames: "",
        accountFirstName: "",
        accountLastName: "",
        accountIdNumber: "",
        accountEmail: "",
        accountEmail2: undefined,
        accountEmail3: undefined,
        accountEmail4: undefined,
        accountEmail5: undefined,
        accountTelNumberCell: "",
        accountTelNumberHome: "",
        accountTelNumberWork: "",
        accountHomeAddress1: "",
        accountHomeAddress2: "",
        accountHomeAddressCity: "",
        accountHomeAddressProvince: "",
        accountHomeAddressPostalCode: "",
        accountPostalAddress1: "",
        accountPostalAddress2: "",
        accountPostalAddressCity: "",
        accountPostalAddressProvince: "",
        accountPostalAddressPostalCode: "",
        accountEmployer: "",
        accountOccupation: "",
        accountMedicalAid: "",
        accountMedicalAidPlan: "",
        accountMedicalAidNumber: "",
        accountMedicalAidDependents: "",
        accountPractice: "",
        accountPatientId: [],
        accountCreatedBy: "",
        accountCreatedDate: "",
      });
      setPostalAddress(false);
    } catch (err) {
      const msg = err?.response?.data?.message || "Account could not be saved";
      showToast("error", msg);
      // On a number conflict, unlock the field so the user can enter a different number
      if (err?.response?.status === 409) {
        setPatientAccount((prev) => ({ ...prev, accountNumber: "" }));
        setNumberCandidates([]);
        setSelectedCandidate(null);
      }
    }
  }

  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (token) {
      const decoded = jwtDecode(token);
      const roles = Array.isArray(decoded.role)
        ? decoded.role
        : [decoded.role].filter(Boolean);
      setIsSysAdmin(roles.includes("SystemAdmin"));
    }
  }, []);

  useEffect(() => {
    if (!show || !account?.accountPractice) return;
    const practiceNums = [].concat(account.accountPractice);
    getPracticesByNumbers(practiceNums).then((result) => {
      if (Array.isArray(result)) setPractices(result);
    });
  }, [show, account]);

  useEffect(() => {
    if (!show) {
      setNumberCandidates([]);
      setSelectedCandidate(null);
      setValidationError("");
      setPractices([]);
    }
  }, [show]);

  useEffect(() => {
    if (!postalAddress) {
      setPatientAccount((prevPatientAccount) => ({
        ...prevPatientAccount,
        accountPostalAddress1: patientAccount.accountHomeAddress1,
        accountPostalAddress2: patientAccount.accountHomeAddress2,
        accountPostalAddressCity: patientAccount.accountHomeAddressCity,
        accountPostalAddressProvince: patientAccount.accountHomeAddressProvince,
        accountPostalAddressPostalCode:
          patientAccount.accountHomeAddressPostalCode,
      }));
    }
    if (postalAddress) {
      setPatientAccount((prevPatientAccount) => ({
        ...prevPatientAccount,
        accountPostalAddress1: "",
        accountPostalAddress2: "",
        accountPostalAddressCity: "",
        accountPostalAddressProvince: "",
        accountPostalAddressPostalCode: "",
      }));
    }
  }, [
    patientAccount.accountHomeAddress1,
    patientAccount.accountHomeAddress2,
    patientAccount.accountHomeAddressCity,
    patientAccount.accountHomeAddressProvince,
    patientAccount.accountHomeAddressPostalCode,
    postalAddress,
    account,
  ]);

  const EXTRA_EMAIL_FIELDS = [
    "accountEmail2",
    "accountEmail3",
    "accountEmail4",
    "accountEmail5",
  ];

  // Rendering the content of the forms based on the current step
  const renderContent = () => {
    return (
      <Form>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group as={Col} controlId="formGridPatientnumber">
              <Form.Label>Account Number</Form.Label>
              <Form.Control
                required
                name="accountNumber"
                value={patientAccount.accountNumber || ""}
                onChange={handleChangeAccount}
                readOnly={isMethod3 && !!patientAccount.accountNumber}
                placeholder={isMethod3 && !patientAccount.accountNumber ? "Generated on save" : "Custom Patient Number"}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-1">
          <Col md={2}>
            <Form.Group as={Col} controlId="formGridAccountTitle">
              <Form.Label>Title</Form.Label>
              <Form.Select
                required
                defaultValue=""
                name="accountTitle"
                value={patientAccount.accountTitle}
                onChange={handleChangeAccount}
              >
                <option></option>
                <option>Ms</option>
                <option>Mr</option>
                <option>Mrs</option>
                <option>Dr</option>
                <option>Prof</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group as={Col} controlId="formGridAccountFullNames">
              <Form.Label>Full Name(s)</Form.Label>
              <Form.Control
                required
                name="accountFullNames"
                value={patientAccount.accountFullNames}
                onChange={handleChangeAccount}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group as={Col} controlId="formGridAccountLastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                required
                name="accountLastName"
                value={patientAccount.accountLastName}
                onChange={handleChangeAccount}
              />
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group as={Col} controlId="formGridAccountInitials">
              <Form.Label>Initials</Form.Label>
              <Form.Control
                name="accountInitials"
                value={patientAccount.accountInitials}
                onChange={handleChangeAccount}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group as={Col} controlId="formGridaccountFirstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                required
                value={patientAccount.accountFirstName}
                name="accountFirstName"
                onChange={handleChangeAccount}
              />
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group as={Col} controlId="formGridAccountIdNumber">
              <Form.Label>ID Number</Form.Label>
              <Form.Control
                required
                value={patientAccount.accountIdNumber}
                name="accountIdNumber"
                onChange={handleChangeAccount}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="formGridaccountCellPhone">
              <Form.Label>Mobile Number</Form.Label>
              <Form.Control
                required
                value={patientAccount.accountTelNumberCell}
                name="accountTelNumberCell"
                onChange={handleChangeAccount}
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            {/* Primary Email */}
            <Form.Group controlId="formGridAccountEmailAddress">
              <Form.Label>Email</Form.Label>
              <InputGroup className="align-items-center">
                <Form.Control
                  required
                  value={patientAccount.accountEmail}
                  name="accountEmail"
                  onChange={handleChangeAccount}
                />
                <Button
                  variant="link"
                  className="text-primary d-flex align-items-center"
                  onClick={() => {
                    const nextField = EXTRA_EMAIL_FIELDS.find(
                      (f) => patientAccount[f] === undefined,
                    );
                    if (nextField) {
                      handleChangeAccount({
                        target: { name: nextField, value: "" },
                      });
                    }
                  }}
                  disabled={EXTRA_EMAIL_FIELDS.every(
                    (f) => patientAccount[f] !== undefined,
                  )}
                >
                  <FaPlus />
                </Button>
              </InputGroup>
            </Form.Group>

            {/* Additional Emails */}
            {EXTRA_EMAIL_FIELDS.map((field, idx) => {
              if (patientAccount[field] === undefined) return null;

              return (
                <Form.Group key={field} className="mt-2">
                  <InputGroup className="align-items-center">
                    <Form.Control
                      value={patientAccount[field]}
                      name={field}
                      placeholder={`Additional Email ${idx + 2}`}
                      onChange={handleChangeAccount}
                    />
                    <Button
                      variant="link"
                      className="text-danger ms-0 d-flex align-items-center"
                      onClick={() =>
                        handleChangeAccount({
                          target: { name: field, value: undefined },
                        })
                      }
                    >
                      <FaTrash />
                    </Button>
                  </InputGroup>
                </Form.Group>
              );
            })}
          </Col>
        </Row>
        <hr />
        {/* Row for Home Address Line 1 and Line 2 */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="formGridEmployer">
              <Form.Label>Employer</Form.Label>
              <Form.Control
                required
                value={patientAccount.accountEmployer}
                name="accountEmployer"
                onChange={handleChangeAccount}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formGridOccupation">
              <Form.Label>Occupation</Form.Label>
              <Form.Control
                required
                value={patientAccount.accountOccupation}
                name="accountOccupation"
                onChange={handleChangeAccount}
              />
            </Form.Group>
          </Col>
          <Form.Group controlId="formGridWorkTelNumber">
            <Form.Label>Work Phone Number</Form.Label>
            <Form.Control
              required
              value={patientAccount.accountTelNumberWork}
              name="accountTelNumberWork"
              onChange={handleChangeAccount}
            />
          </Form.Group>
        </Row>
        {/* Horizontal Line */}
        <hr />
        {/* Row for Home Address Line 1 and Line 2 */}
        <Row className="mb-3">
          <Form.Group controlId="formGridWorkTelNumber">
            <Form.Label>Home Phone Number</Form.Label>
            <Form.Control
              required
              value={patientAccount.accountTelNumberHome}
              name="accountTelNumberHome"
              onChange={handleChangeAccount}
            />
          </Form.Group>
          <Col md={6}>
            <Form.Group controlId="formGridHomeAddress">
              <Form.Label>Home Address 1</Form.Label>
              <Form.Control
                required
                value={patientAccount.accountHomeAddress1}
                name="accountHomeAddress1"
                onChange={handleChangeAccount}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formGridHomeAddress2">
              <Form.Label>Home Address 2</Form.Label>
              <Form.Control
                required
                value={patientAccount.accountHomeAddress2}
                name="accountHomeAddress2"
                onChange={handleChangeAccount}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Row for City and Province */}
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group controlId="formGridHomeAddressCity">
              <Form.Label>City</Form.Label>
              <Form.Control
                required
                value={patientAccount.accountHomeAddressCity}
                name="accountHomeAddressCity"
                onChange={handleChangeAccount}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group as={Col} controlId="formGridaccountHomeAddressProvince">
              <Form.Label>Province</Form.Label>
              <Form.Select
                defaultValue="Choose..."
                value={patientAccount.accountHomeAddressProvince}
                name="accountHomeAddressProvince"
                onChange={handleChangeAccount}
              >
                <option>Choose...</option>
                <option>Eastern Cape</option>
                <option>Free State</option>
                <option>Gauteng</option>
                <option>KwaZulu-Natal</option>
                <option>Limpopo</option>
                <option>Mpumalanga</option>
                <option>North West</option>
                <option>Northern Cape</option>
                <option>Western Cape</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="formGridaccountHomeAddress">
              <Form.Label>Postal Code</Form.Label>
              <Form.Control
                required
                value={patientAccount.accountHomeAddressPostalCode}
                name="accountHomeAddressPostalCode"
                onChange={handleChangeAccount}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-1 mt-3">
          <Form.Group as={Col} controlId="checkboxGroup">
            <Form.Check
              type="switch"
              label="The postal address is different from the home address"
              name="accountPostal"
              value="false"
              onChange={handleChangeAccount}
            />
          </Form.Group>
        </Row>
        {postalAddress && (
          <>
            <Row>
              <Col md={6}>
                <Form.Group controlId="formGridPostalAddress1">
                  <Form.Label>Postal Address 1</Form.Label>
                  <Form.Control
                    required
                    value={patientAccount.accountPostalAddress1}
                    name="accountPostalAddress1"
                    onChange={handleChangeAccount}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formGridPostAddress2">
                  <Form.Label>Home Address 2</Form.Label>
                  <Form.Control
                    required
                    value={patientAccount.accountPostalAddress2}
                    name="accountPostalAddress2"
                    onChange={handleChangeAccount}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row for City and Province */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="formGridPostAddressCity">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    required
                    value={patientAccount.accountPostalAddressCity}
                    name="accountPostalAddressCity"
                    onChange={handleChangeAccount}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group as={Col} controlId="formPostState">
                  <Form.Label>Province</Form.Label>
                  <Form.Select
                    defaultValue="Choose..."
                    value={patientAccount.accountPostalAddressProvince}
                    name="accountPostalAddressProvince"
                    onChange={handleChangeAccount}
                  >
                    <option>Choose...</option>
                    <option>Eastern Cape</option>
                    <option>Free State</option>
                    <option>Gauteng</option>
                    <option>KwaZulu-Natal</option>
                    <option>Limpopo</option>
                    <option>Mpumalanga</option>
                    <option>North West</option>
                    <option>Northern Cape</option>
                    <option>Western Cape</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="formGridPostAddressPostal">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    required
                    value={patientAccount.accountPostalAddressPostalCode}
                    name="accountPostalAddressPostalCode"
                    onChange={handleChangeAccount}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        )}
        {isSysAdmin && (
          <>
            <hr />
            <Row className="mb-1 align-items-center">
              <Col>
                <span
                  className="text-muted fw-semibold"
                  style={{ fontSize: "0.8rem" }}
                >
                  MEDIKREDIT PLAN NUMBERS
                </span>
              </Col>
              <Col xs="auto">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-secondary"
                  title="View Medical Aids reference table"
                  onClick={() => setShowMedAidRef(true)}
                >
                  <i className="fa-solid fa-circle-info" />
                </Button>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="accFormGridMK03">
                  <Form.Label style={{ fontSize: "0.82rem" }}>
                    Plan Number 03
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    name="accountMediKreditPlanNumber03"
                    value={patientAccount.accountMediKreditPlanNumber03 || ""}
                    onChange={handleChangeAccount}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="accFormGridMK04">
                  <Form.Label style={{ fontSize: "0.82rem" }}>
                    Plan Number 04
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    name="accountMediKreditPlanNumber04"
                    value={patientAccount.accountMediKreditPlanNumber04 || ""}
                    onChange={handleChangeAccount}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="accFormGridMK06">
                  <Form.Label style={{ fontSize: "0.82rem" }}>
                    Plan Number 06
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    name="accountMediKreditPlanNumber06"
                    value={patientAccount.accountMediKreditPlanNumber06 || ""}
                    onChange={handleChangeAccount}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        )}
      </Form>
    );
  };

  return (
    <>
      <AppToast
        show={toast.show}
        variant={toast.variant}
        message={toast.message}
        onClose={hideToast}
      />
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header
          closeButton
          className="border-secondary"
        >
          <div className="w-100">
            <Modal.Title
              id="contained-modal-title-vcenter"
              className="text-primary fs-5"
            >
              Update Account Information
              <Row className="fw-normal fs-6 fw-normal">
                <Form.Text>
                  Please enter the account information in the form below.
                </Form.Text>
              </Row>
            </Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="step-content">{renderContent()}</div>
        </Modal.Body>
        <Modal.Footer>
          {numberCandidates.length > 1 && (
            <Form.Group className="mb-3 w-100">
              <Form.Label>Select account number</Form.Label>
              {numberCandidates.map((c, i) => (
                <Form.Check
                  key={i}
                  type="radio"
                  name="candidateSelection"
                  label={`${c.candidate} (Practice ${c.practiceNumber})`}
                  value={c.candidate}
                  checked={selectedCandidate === c.candidate}
                  onChange={() => setSelectedCandidate(c.candidate)}
                />
              ))}
            </Form.Group>
          )}
          {validationError && (
            <div className="text-danger mb-2 w-100 text-center">{validationError}</div>
          )}
          <Button
            variant="primary w-25"
            onClick={handleSave}
            className="w-25 ms-3 d-flex align-items-center justify-content-center gap-2"
          >
            <i class="fa-regular fa-floppy-disk"></i>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
      <MedicalAidsReferenceModal
        show={showMedAidRef}
        onHide={() => setShowMedAidRef(false)}
      />
    </>
  );
}

export function EditAccount({ account }) {
  const [accountModalShow, setAccountModalShow] = useState(false);

  return (
    <>
      <Button
        variant="primary"
        className="w-50 d-flex align-items-center justify-content-center gap-3"
        onClick={() => setAccountModalShow(true)}
      >
        <i className="fa-regular fa-pen-to-square"></i>
        Edit Account
      </Button>
      <AccountEditModal
        show={accountModalShow}
        account={account}
        onHide={() => setAccountModalShow(false)}
      />
    </>
  );
}
